/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import IconButton from '@atlaskit/button/icon/button';
import {
	redo as redoKeymap,
	ToolTipContent,
	undo as undoKeymap,
} from '@atlaskit/editor-common/keymaps';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';
import type { PromptEditorProps } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import { RovoPromptScreenWithLogic } from '../../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';

import { ActionButtons } from './ActionButtons';
import { renderRedoIcon, renderUndoIcon, renderViewChangesIcon } from './icons';
import { messages } from './messages';

// The embedded prompt renders frameless, which zeroes the padding the standard
// prompt surface applies. Re-applying it here keeps the PSR input height in line
// with the standard prompt.
const askRovoCSS = css({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingInline: token('space.200'),
	paddingBlock: token('space.150'),
});

const promptSlotCSS = css({
	flex: 1,
	minWidth: 0,
});

// Animated wrapper for the review controls. It collapses its width (grid
// `1fr` → `0fr`, which animates to the content's natural width — no fixed bound
// needed) and fades when the user starts composing a re-prompt, and expands +
// fades back in when the prompt is cleared. The controls are kept mounted (not
// conditionally rendered) so the slide-OUT can play; `visibility` flips only
// after the collapse finishes so they drop out of the tab order / accessibility
// tree while hidden. Motion is disabled under `prefers-reduced-motion`.
const rightActionsWrapperCSS = css({
	display: 'grid',
	flexShrink: 0,
	transitionProperty: 'grid-template-columns, opacity, visibility',
	transitionDuration: '0.2s, 0.2s, 0s',
	transitionTimingFunction: 'ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transitionDuration: '0s',
	},
});

const rightActionsVisibleCSS = css({
	gridTemplateColumns: '1fr',
	opacity: 1,
	visibility: 'visible',
	transitionDelay: '0s',
});

const rightActionsHiddenCSS = css({
	gridTemplateColumns: '0fr',
	opacity: 0,
	visibility: 'hidden',
	pointerEvents: 'none',
	// Delay only the `visibility` switch until the collapse has finished so the
	// controls stay rendered (and animating) until they are fully collapsed.
	transitionDelay: '0s, 0s, 0.2s',
});

// The controls row itself. `overflow: hidden` + `min-width: 0` let it be clipped
// as the grid column collapses to `0fr`.
const rightActionsCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	overflow: 'hidden',
	minWidth: 0,
});

export interface AskRovoProps {
	appearance: EditorAppearance;
	cancel: (_options?: {
		shouldClearSelectionContext?: boolean;
		shouldIgnoreNextSpaceTrigger?: boolean;
	}) => void;
	canGoNext: boolean;
	canGoPrevious: boolean;
	canRedo: boolean;
	canUndo: boolean;
	canViewChanges: boolean;
	hasFallbackPrompt: boolean;
	/**
	 * Whether the prompt currently holds any user input (text, mention or inline
	 * card). When `true` the review controls (step-through, show/hide changes,
	 * undo/redo) are hidden and the Rovo prompt's own add + submit controls are
	 * shown instead, so the toolbar focuses on composing the re-prompt.
	 */
	hasInputText: boolean;
	hasRovoPrompt: boolean;
	isAIFC: boolean | undefined;
	isStepThroughExpanded: boolean;
	onNext: () => void;
	onPrevious: () => void;
	/**
	 * Reports prompt emptiness from the Rovo prompt (which has no `onTextChange`).
	 * The parent uses it to keep `textInput` in sync so "press Enter to accept"
	 * can tell whether the user has typed a re-prompt.
	 */
	onPromptContentChange: (payload: PromptContentChangePayload) => void;
	onRedo: () => void;
	onTextChange: (input: string) => void;
	onToggleStepThrough: () => void;
	onUndo: () => void;
	onViewChanges: () => void;
	openInRovoChat: boolean | undefined;
	openInRovoChatConfig: { onBeforeSubmit: (prompt: DocNode) => boolean };
	PromptEditor: ((props: PromptEditorProps) => JSX.Element) | undefined;
	segmentIndex: number;
	/**
	 * Whether DELETED content is currently revealed in the always-on diff overlay.
	 * `false` = deletions hidden (default review view); `true` = deletions shown.
	 * Drives the show/hide-changes toggle's icon, label and selected state.
	 */
	showDeletedChanges: boolean;
	submit: () => void;
	textInput: string;
	totalSegments: number;
}

/**
 * The prompt input row for the Post Stream Review screen. Embeds the Rovo (or
 * fallback) prompt and hosts the right-hand controls — step-through navigation,
 * the show/hide changes toggle and Undo/Redo — inline alongside it.
 *
 * While the prompt is empty the review controls are shown. Once the user starts
 * composing a re-prompt (`hasInputText`), those controls slide + fade out and
 * the Rovo prompt's own add + submit controls take their place, so the toolbar
 * focuses on the prompt.
 */
export function AskRovo({
	hasRovoPrompt,
	hasFallbackPrompt,
	hasInputText,
	openInRovoChat,
	openInRovoChatConfig,
	cancel,
	PromptEditor,
	textInput,
	onTextChange,
	onPromptContentChange,
	submit,
	appearance,
	isAIFC,
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	canViewChanges,
	showDeletedChanges,
	onViewChanges,
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	onPrevious,
	onNext,
	isStepThroughExpanded,
	onToggleStepThrough,
}: AskRovoProps): JSX.Element {
	const intl = useIntl();

	// Custom tooltip content appending the keyboard shortcut to the Undo/Redo
	// labels (e.g. "Undo ⌘Z"). Memoized so the `tooltip` prop object is stable
	// across renders (`@atlassian/perf-linting/no-unstable-inline-props`).
	const undoTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent description={intl.formatMessage(messages.undo)} keymap={undoKeymap} />
			),
		}),
		[intl],
	);
	const redoTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent description={intl.formatMessage(messages.redo)} keymap={redoKeymap} />
			),
		}),
		[intl],
	);

	// Render the Rovo prompt unless we have a fallback editor to show.
	const shouldRenderFallbackPrompt =
		!(openInRovoChat && hasRovoPrompt) && hasFallbackPrompt && !!PromptEditor;

	return (
		<div css={askRovoCSS} data-testid="post-stream-review-ask-rovo">
			<div css={promptSlotCSS}>
				{shouldRenderFallbackPrompt && PromptEditor ? (
					<PromptScreen
						textInput={textInput}
						onTextChange={onTextChange}
						submit={submit}
						cancel={cancel}
						PromptEditor={PromptEditor}
						appearance={appearance}
						isAIFC={isAIFC}
					/>
				) : (
					<RovoPromptScreenWithLogic
						cancel={cancel}
						openInRovoChatConfig={hasRovoPrompt ? openInRovoChatConfig : undefined}
						autoClose={false}
						isPostStreamReview={true}
						onPromptContentChange={onPromptContentChange}
						rovoInlineChatAppearance="frameless"
						showTrailingActions={hasInputText}
					/>
				)}
			</div>
			{/* Review controls are visible only while the prompt is empty. Once the user
			    starts composing a re-prompt they slide + fade out (and the Rovo prompt's
			    own add + submit controls, revealed via `showTrailingActions`, take over);
			    clearing the prompt slides them back in. Kept mounted for the exit
			    animation and hidden from pointer/AT via `visibility` once collapsed. */}
			<div
				css={[
					rightActionsWrapperCSS,
					hasInputText ? rightActionsHiddenCSS : rightActionsVisibleCSS,
				]}
				aria-hidden={hasInputText || undefined}
				data-testid="post-stream-review-controls"
			>
				<div css={rightActionsCSS}>
					<ActionButtons
						totalSegments={totalSegments}
						segmentIndex={segmentIndex}
						canGoPrevious={canGoPrevious}
						canGoNext={canGoNext}
						isStepThroughExpanded={isStepThroughExpanded}
						onPrevious={onPrevious}
						onNext={onNext}
						onToggleStepThrough={onToggleStepThrough}
					/>
					{/* IconButton tooltips are opt-in (`isTooltipDisabled` defaults to true). These
				    controls surface their `label` on hover — including the view-changes toggle's
				    "Compare with original"/"Only show suggestion" states. Undo/Redo pass a custom tooltip `content`
				    that appends the keyboard shortcut (e.g. "Undo ⌘Z") via `ToolTipContent`, while
				    `label` stays the plain accessible name. Shortcuts handled in
				    `useReviewKeyboardShortcuts` (hooks.ts) — keep the two in sync. */}
					<IconButton
						appearance="subtle"
						icon={renderViewChangesIcon}
						label={intl.formatMessage(
							showDeletedChanges ? messages.hideChanges : messages.viewChanges,
						)}
						isTooltipDisabled={false}
						isSelected={showDeletedChanges}
						onClick={onViewChanges}
						isDisabled={!canViewChanges}
						testId="post-stream-review-view-changes"
					/>
					<IconButton
						appearance="subtle"
						icon={renderUndoIcon}
						label={intl.formatMessage(messages.undo)}
						isTooltipDisabled={false}
						tooltip={undoTooltip}
						isDisabled={!canUndo}
						onClick={onUndo}
						testId="post-stream-review-undo"
					/>
					<IconButton
						appearance="subtle"
						icon={renderRedoIcon}
						label={intl.formatMessage(messages.redo)}
						isTooltipDisabled={false}
						tooltip={redoTooltip}
						isDisabled={!canRedo}
						onClick={onRedo}
						testId="post-stream-review-redo"
					/>
				</div>
			</div>
		</div>
	);
}
